const BASE_URL = process.env.REACT_APP_API_URL;

// Fetch a single book (previously from favorites)
export async function fetchFavoriteBook(bookId) {
  const res = await fetch(`${BASE_URL}/book/${bookId}`);
  if (!res.ok) throw new Error("Failed to fetch book details");
  return res.json();
}

// USERS
export async function registerUser(username, email, password) {
  const res = await fetch(`${BASE_URL}/register`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, email, password }),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Registration failed");
  return data;
}

export async function loginUser(identifier, password) {
  const res = await fetch(`${BASE_URL}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username: identifier, password }),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Login failed");
  return data;
}

export async function sendPasswordResetEmail(identifier) {
  const res = await fetch(`${BASE_URL}/send-reset-email`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ identifier }),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed to send reset email");
  return data;
}

export async function resetPassword(token, password) {
  const res = await fetch(`${BASE_URL}/reset-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, password }),
  });

  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Reset failed");
  return data;
}

// FAVORITES (Now handled by /book/save and GET /books/favorites/:id)
export async function addFavorite(userId, book, userRating = null) {
  const res = await fetch(`${BASE_URL}/book/save`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      book,
      userRating: userRating ?? null,
      is_favorite: true,
    }),
  });
  return res.json();
}

export async function getFavorites(userId) {
  const res = await fetch(`${BASE_URL}/books/favorites/${userId}`);
  return res.json();
}

export async function deleteFavorite(userId, book_id) {
  const res = await fetch(`${BASE_URL}/favorites`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId, book_id }),
  });
  return res.json();
}

// RATINGS (Now handled by /book/save and GET /books/ratings/:id)
export async function addOrUpdateRating(userId, book, userRating) {
  const res = await fetch(`${BASE_URL}/book/save`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      book,
      userRating,
      is_favorite: null,  // don’t overwrite favorite status
    }),
  });
  return res.json();
}

export async function getRatings(userId) {
  const res = await fetch(`${BASE_URL}/books/ratings/${userId}`);
  return res.json();
}

export async function deleteRating(userId, book_id) {
  const res = await fetch(`${BASE_URL}/book/save`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: userId,
      book: { id: book_id },
      userRating: null,
      is_favorite: null,  // preserve favorite status
    }),
  });
  return res.json();
}


export async function fetchAverageRating(bookId) {
  try {
    const response = await fetch(`${BASE_URL}/average-rating/${bookId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch average rating');
    }
    const data = await response.json();
    return {
      averageRating: data.average_rating,
      ratingsCount: data.ratings_count,
    };
  } catch (error) {
    console.error("Error fetching average rating:", error);
    throw error;
  }
}

// FOLLOWING SYSTEM
export async function searchUsers(query) {
  const res = await fetch(`${BASE_URL}/users/search?q=${encodeURIComponent(query)}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Search failed");
  return data;
}

export async function followUser(followerId, userId) {
  const res = await fetch(`${BASE_URL}/follow`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId, follower_id: followerId }),
  });
  return res.json();
}

export async function unfollowUser(followerId, userId) {
  const res = await fetch(`${BASE_URL}/unfollow`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId, follower_id: followerId }),
  });
  return res.json();
}

export async function getFollowers(userId) {
  const res = await fetch(`${BASE_URL}/followers/${userId}`);
  return res.json();
}

export async function getFollowing(userId) {
  const res = await fetch(`${BASE_URL}/following/${userId}`);
  return res.json();
}

// PROFILE
export async function getUserInfo(userId) {
  const res = await fetch(`${BASE_URL}/users/${userId}`);
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Could not fetch user info");
  return data;
}

export async function updateUserProfile(userId, bio, profile_pic) {
  const res = await fetch(`${BASE_URL}/users/${userId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bio, profile_pic }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Could not update profile");
  return data;
}

// REVIEWS
export async function createReview(userId, bookId, content) {
  const res = await fetch(`${BASE_URL}/reviews`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId, book_id: bookId, content }),
  });
  if (!res.ok) throw new Error("Failed to create review");
  return res.json();
}

export async function updateReview(userId, bookId, content) {
  const res = await fetch(`${BASE_URL}/reviews/${bookId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId, content }),
  });
  return res.json();
}

export async function getReviewsForBook(bookId) {
  const res = await fetch(`${BASE_URL}/reviews/${bookId}`);
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Failed to fetch reviews: ${errText}`);
  }
  return res.json();
}

export async function deleteReview(userId, bookId) {
  const res = await fetch(`${BASE_URL}/reviews/${bookId}?user_id=${userId}`, {
    method: "DELETE",
  });
  return res.json();
}


// RECOMMENDATIONS
export async function getRecommendations(userId) {
  const res = await fetch(`${BASE_URL}/recommendations/${userId}`)
  if (!res.ok) {
    throw new Error("Failed to fetch recommendations")
  }
  return res.json()
}

export async function getPopularBooks(limit = 20) {
  const res = await fetch(`${BASE_URL}/popular-books?limit=${limit}`)
  if (!res.ok) {
    throw new Error("Failed to fetch popular books")
  }
  return res.json()
}

export async function getSimilarBooks(bookId, limit = 10) {
  const res = await fetch(`${BASE_URL}/similar-books/${bookId}?limit=${limit}`)
  if (!res.ok) {
    throw new Error("Failed to fetch similar books")
  }
  return res.json()
}
